import { PageBanner, Section, SectionTitle, EnquiryForm, useDocument } from '@longnan/ui';
import { assetUrl, useT } from '../i18n';
import { creditOf, findImage, useSiteData } from '../useSiteData';
import {} from './common';
import type { CareersDoc } from '../types';

export function Careers() {
  const { t, pick } = useT();
  const s = useSiteData();
  const doc = useDocument<CareersDoc>('group', 'careers.json');
  const error = s.error ?? doc.error;
  if (error) return <div className="mx-auto max-w-6xl px-5 py-24 text-danger-light">{error}</div>;
  if (!s.data || !doc.data) return <div className="mx-auto max-w-6xl px-5 py-24 text-muted">{t('state.loading')}</div>;

  const jobs = doc.data.social;
  const lineName = (line: string) =>
    line === 'group'
      ? { zh: t('brand.name'), en: t('brand.name') }
      : (s.data!.eco.subsidiaries.find((x) => x.key === line)?.shortName ?? { zh: line, en: line });
  const img = findImage(s.data.imagery, 'hero');

  return (
    <>
      <PageBanner
        image={assetUrl(img.local)}
        imageAlt={pick(img.alt)}
        imageCredit={creditOf(img)}
        eyebrow={t('careers.eyebrow')}
        title={t('careers.title')}
        lead={t('careers.lead')}
      />

      <Section id="jobs">
        <SectionTitle id="jobs" title={t('careers.social.title')} lead={t('careers.social.lead')} />
        <ul className="mt-12 divide-y divide-line border-y border-line">
          {jobs.map((j) => (
            <li key={j.id} className="grid gap-4 py-6 md:grid-cols-[1fr_auto] md:items-center">
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="rounded bg-surface px-2 py-0.5 text-xs text-muted-light">{pick(lineName(j.line))}</span>
                </div>
                <h3 className="mt-3 text-lg font-semibold text-body">{pick(j.title)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted">{pick(j.summary)}</p>
              </div>
              <div className="text-sm text-muted md:text-right">
                <div>{pick(j.location)}</div>
                <div>{pick(j.type)}</div>
                <a href="#apply" className="mt-3 inline-flex font-semibold text-primary-light hover:underline">
                  {t('careers.apply')} →
                </a>
              </div>
            </li>
          ))}
        </ul>
      </Section>

      <Section id="campus" tone="surface">
        <SectionTitle id="campus" title={pick(doc.data.campus.title)} lead={pick(doc.data.campus.body)} />
      </Section>

      <Section id="apply">
        <SectionTitle id="apply" title={t('careers.form.title')} />
        <div className="mt-12 max-w-3xl">
          <EnquiryForm
            idPrefix="career"
            fields={[
              { id: 'name', label: { zh: t('form.name'), en: t('form.name') }, type: 'text', required: true },
              { id: 'phone', label: { zh: t('form.phone'), en: t('form.phone') }, type: 'tel', required: true },
              { id: 'email', label: { zh: t('form.email'), en: t('form.email') }, type: 'email', required: true },
              {
                id: 'role',
                label: { zh: t('form.role'), en: t('form.role') },
                type: 'select',
                required: true,
                options: jobs.map((j) => ({ value: j.id, label: j.title })),
              },
              { id: 'message', label: { zh: t('form.message'), en: t('form.message') }, type: 'textarea' },
            ]}
            submit={{ zh: t('form.submit'), en: t('form.submit') }}
            success={{ zh: t('careers.form.success'), en: t('careers.form.success') }}
            note={{ zh: t('form.note'), en: t('form.note') }}
            requiredHint={{ zh: t('form.required'), en: t('form.required') }}
            selectPlaceholder={{ zh: t('form.select'), en: t('form.select') }}
          />
        </div>
      </Section>
    </>
  );
}
